import { signOut } from '@backoffice-frontend/authentication'
import { ENV, Environment } from '@backoffice-frontend/environment'
import { MoiaLogo } from '@backoffice-frontend/patterns'
import { Button, Callout, Card, Text } from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const baseDomains: Partial<Record<Environment, string>> = {
  [Environment.development]: '.backoffice.dev.moia-group.io',
  [Environment.integration]: '.backoffice.int.moia-group.io',
  [Environment.production]: '.backoffice.moia-group.io',
}

const identityProviderApiBaseUrl: Partial<Record<Environment, string>> = {
  [Environment.development]:
    'https://cognito-identity-provider.backoffice.dev.moia-group.io',
  [Environment.integration]:
    'https://cognito-identity-provider.backoffice.int.moia-group.io',
  [Environment.production]:
    'https://cognito-identity-provider.backoffice.prd.moia-group.io',
}

type WrongSubdomainViewProps = {
  userEmail: string
}

export const WrongSubdomainView = ({ userEmail }: WrongSubdomainViewProps) => {
  const { t } = useTranslation()
  const [correctUrl, setCorrectUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    const fetchCorrectUrl = async () => {
      const apiBaseUrl = identityProviderApiBaseUrl[ENV]
      const baseDomain = baseDomains[ENV]

      if (!apiBaseUrl || !baseDomain) return

      try {
        const response = await fetch(
          `${apiBaseUrl}/backoffice-url?email=${encodeURIComponent(userEmail)}`,
        )

        if (!response.ok) {
          setError(true)
          return
        }

        const data = (await response.json()) as { subdomain: string }
        if (data.subdomain) {
          const currentPath = window.location.pathname
          setCorrectUrl(`https://${data.subdomain}${baseDomain}${currentPath}`)
        }
      } catch {
        setError(true)
      }
    }

    fetchCorrectUrl()
  }, [userEmail])

  const handleRedirect = async () => {
    if (!correctUrl) return
    await signOut()
    window.location.href = correctUrl
  }

  return (
    <>
      <title>{t('authenticatedAppView_wrongSubdomain_title')}</title>
      <div
        css={{
          minHeight: '100vh',
          backgroundSize: 'cover',
          alignItems: 'flex-start',
          justifyItems: 'center',
          backgroundImage: 'url(/assets/images/login.png)',
          display: 'grid',
        }}
      >
        <MoiaLogo
          css={theme => ({
            width: '160px',
            marginTop: theme.spacing(6),
            color: theme.palette.common.white,
          })}
        />
        <Card
          css={theme => ({
            padding: theme.spacing(3),
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
          })}
          outline
        >
          <Callout variant="warning" hideDismiss>
            <Text>{t('authenticatedAppView_wrongSubdomain_message')}</Text>
          </Callout>
          {error && (
            <Callout variant="critical" hideDismiss>
              <Text>{t('authenticatedAppView_wrongSubdomain_error')}</Text>
            </Callout>
          )}
          <Button
            onClick={handleRedirect}
            label={t('authenticatedAppView_wrongSubdomain_redirectButton')}
            disabled={!correctUrl}
          />
        </Card>
      </div>
    </>
  )
}
